'use client'
import { BigNumber } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { getAddress } from '@ethersproject/address'

import { Contract } from '@ethersproject/contracts'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { ChainId, CurrencyAmount, JSBI, MON, Percent, TokenAmount, Token, NativeCurrency, Pair } from '@monadex/sdk'
import { isAddress as isViemAddress } from 'viem'
import truncateEthAddress from 'truncate-eth-address'
import { SUPPORTED_CHAINIDS, GlobalData, MIN_NATIVE_CURRENCY_FOR_GAS } from '@/constants'
import { ethers, providers } from 'ethers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TokenAddressMap } from '@/state/list/hooks'
import { GET_BLOCKS } from '@/data/apollo/queries'
import { blockClient } from '@/data/apollo/client'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { useAppKitNetwork, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react'
import { AppKitNetwork } from '@reown/appkit/networks'
// import { TokenAddressMap } from '../state/lists/hooks'

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress (address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) { // eslint-disable-line
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return truncateEthAddress(address)
}

export function isAddress (value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

// add 100%
export function calculateGasMargin (value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(2))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent (num: number): Percent {
  return new Percent(JSBI.BigInt(num), JSBI.BigInt(10000))
}

export function currencyId (currency: Token | NativeCurrency): string {
  if (currency === MON) return 'MON'
  if (currency instanceof Token) return currency.address
  throw new Error('invalid currency')
}

export function calculateSlippageAmount (value: TokenAmount, slippage: number): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 - slippage)), JSBI.BigInt(10000)),
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 + slippage)), JSBI.BigInt(10000))
  ]
}

// account is not optional
export function getSigner (library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner (library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  if (account !== undefined && account != null && account !== '') {
    return library.getSigner(account)
  } else {
    return library
  }
}

// account is optional
export function getContract (address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isViemAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

export function escapeRegExp (string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export interface WalletData {
  account: string | undefined
  chainId: ChainId
  isConnected: boolean
  findProvider: Web3Provider | undefined
  provider: Web3Provider | undefined
  switchNetwork: (network: AppKitNetwork) => void
  baseChain: number
}

export function useWalletData (): WalletData {
  const { address: account, isConnected } = useAppKitAccount()
  const { chainId: id, switchNetwork } = useAppKitNetwork()
  const { walletProvider } = useAppKitProvider('eip155')
  const toNum = Number(id)
  const chainId: ChainId = useMemo(() => {
    if (!id) {
      const localChain = localStorage.getItem('localChainId')
      if (localChain) {
        return Number(localChain) as ChainId
      } else {
        return ChainId.MONAD_TESTNET
      }
    }
    if (!SUPPORTED_CHAINIDS.includes(Number(id))) {
      return ChainId.MONAD_TESTNET
    }
    return id
  }, [id]) as ChainId
  const provider = walletProvider
    ? new providers.Web3Provider(walletProvider)
    : undefined
  return {
    account,
    baseChain: toNum,
    chainId,
    isConnected,
    findProvider: provider,
    provider,
    switchNetwork
  }
}

interface BackpackProvider { isBackpack: boolean }
export function isBackpackProvider (provider: unknown): provider is BackpackProvider {
  return typeof provider === 'object' && provider !== null && 'isBackpack' in provider
}

export function isTokensOnList (
  defaultTokens: TokenAddressMap,
  currencies: Array<NativeCurrency | Token | undefined>,
  chainId: ChainId
): boolean[] {
  return currencies.map((currency) => {
    if (currency === MON) return true

    return Boolean(
      currency instanceof Token &&
        defaultTokens[currency.chainId]?.[currency.address]
    )
  })
}
export function formatNumber (
  unformatted: number | string | undefined,
  showDigits = 2
): string | 0 {
  // get fraction digits for small number
  if (!unformatted) return 0
  const absNumber = Math.abs(Number(unformatted))
  if (absNumber > 0) {
    const digits = Math.ceil(Math.log10(1 / absNumber))
    if (digits < 3) {
      return Number(unformatted).toLocaleString('us')
    } else {
      return Number(unformatted).toFixed(digits + showDigits)
    }
  } else {
    return 0
  }
}
export function formatTokenAmount (
  amount?: TokenAmount | CurrencyAmount,
  digits = 2
): string {
  if (amount === undefined) return '-'
  const amountStr = amount.toExact()
  if (Math.abs(Number(amountStr)) > 1) {
    // Use toFixed to respect the digits parameter
    return Number(amountStr).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits
    })
  }
  return amount.toSignificant(digits)
}

export function calculateGasMarginBonus (value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(2))
}

export function getFormattedPrice (price: number): string {
  if (price < 0.001 && price > 0) {
    return '<0.001'
  } else if (price > -0.001 && price < 0) {
    return '>-0.001'
  } else {
    const beforeSign = price > 0 ? '+' : ''
    return beforeSign + price.toLocaleString('us')
  }
}

export function getFormattedPercent (percent: number): string {
  if (percent < 0.001 && percent > 0) {
    return '<+0.001%'
  } else if (percent > -0.001 && percent < 0) {
    return '>-0.001%'
  } else if (percent > 10000) {
    return '>+10000%'
  } else if (percent < -10000) {
    return '<-10000%'
  } else {
    const beforeSign = percent > 0 ? '+' : ''
    return beforeSign + percent.toLocaleString('us') + '%'
  }
}
export function useIsSupportedNetwork (): boolean {
  const { chainId } = useWalletData()
  const checkInclude = SUPPORTED_CHAINIDS.includes(chainId)
  if (checkInclude) {
    return true
  } else {
    return false
  }
}

/**
 * Given the price impact, get user confirmation.
 *
 * @param priceImpactWithoutFee price impact of the trade without the fee.
 */
export function confirmPriceImpactWithoutFee (
  priceImpactWithoutFee: Percent
): boolean {
  if (
    !priceImpactWithoutFee.lessThan(
      GlobalData.percents.PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN
    )
  ) {
    return (
      window.prompt(
        `This swap has a price impact of at least ${priceImpactWithoutFee.toFixed(0)}%. Please type the word 'confirm' to continue with this swap:`
      ) === 'confirm'
    )
  } else if (
    !priceImpactWithoutFee.lessThan(
      GlobalData.percents.ALLOWED_PRICE_IMPACT_HIGH
    )
  ) {
    return window.confirm(
     `Confirm Swap price impact : ${GlobalData.percents.PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN.toFixed(0)}`
    )
  }
  return true
}
export function maxAmountSpend (
  chainId: ChainId,
  currencyAmount?: CurrencyAmount
): CurrencyAmount | undefined {
  if (currencyAmount == null) return undefined
  if (currencyAmount?.currency === MON) {
    if (
      JSBI.greaterThan(currencyAmount.raw, MIN_NATIVE_CURRENCY_FOR_GAS[chainId])
    ) {
      return CurrencyAmount.ether(
        JSBI.subtract(currencyAmount.raw, MIN_NATIVE_CURRENCY_FOR_GAS[chainId])
      )
    } else {
      return CurrencyAmount.ether(JSBI.BigInt(0))
    }
  }
  return currencyAmount
}

export function halfAmountSpend (
  chainId: ChainId,
  currencyAmount?: CurrencyAmount | TokenAmount
): CurrencyAmount | TokenAmount | undefined {
  if (currencyAmount == null) return undefined
  const halfAmount = JSBI.divide(currencyAmount?.raw, JSBI.BigInt(2))
  if (currencyAmount?.currency === MON) {
    if (JSBI.greaterThan(halfAmount, MIN_NATIVE_CURRENCY_FOR_GAS[chainId])) {
      return CurrencyAmount.ether(halfAmount)
    } else {
      return CurrencyAmount.ether(JSBI.BigInt(0))
    }
  }
  return new TokenAmount(currencyAmount?.currency as Token, halfAmount)
}

export async function splitQuery (query: any, localClient: ApolloClient<NormalizedCacheObject>, vars: any, list: any, skipCount = 100) {
  let fetchedData = {}
  let allFound = false
  let skip = 0
  while (!allFound) {
    let end = list.length
    if (skip + skipCount < list.length) {
      end = skip + skipCount
    }
    const sliced = list.slice(skip, end)
    const result = await localClient.query({
      query: query(...vars, sliced),
      fetchPolicy: 'cache-first'
    })
    fetchedData = {
      ...fetchedData,
      ...result.data
    }
    if (Object.keys(result.data).length < skipCount || skip + skipCount > list.length) {
      allFound = true
    } else {
      skip += skipCount
    }
  }

  return fetchedData
}

/**
 * @notice Fetches block objects for an array of timestamps.
 * @dev blocks are returned in chronological order (ASC) regardless of input.
 * @dev blocks are returned at string representations of Int
 * @dev timestamps are returns as they were provided; not the block time.
 * @param {Array} timestamps
 */
export async function getBlocksFromTimestamps (timestamps: any, skipCount = 500) {
  if (timestamps?.length === 0) {
    return []
  }
  const fetchedData: any = await splitQuery(GET_BLOCKS, blockClient, [], timestamps, skipCount)
  const blocks = []
  if (fetchedData) {
    for (const t in fetchedData) {
      if (fetchedData[t].length > 0) {
        blocks.push({
          timestamp: t.split('t')[1],
          number: fetchedData[t][0].number
        })
      }
    }
  }
  return blocks
}

export function shortAddr (address?: string): string {
  if (!address) return ''
  return `${address.slice(0, 4)}...${address.slice(-4)}`
}

export function extractTokenAddresses (searchParams: any) {
  // Create URLSearchParams object if string is passed
  const params = typeof searchParams === 'string'
    ? new URLSearchParams(searchParams)
    : searchParams

  // Extract both currency addresses
  const currency0 = params.get('currency0')
  const currency1 = params.get('currency1')

  // Return as array, maintaining order
  return [currency0, currency1].filter(Boolean)
}

export function userTokenDeposited (userBalance: TokenAmount, totalBalance: TokenAmount, token0: Token, token1: Token, pair: Pair): [TokenAmount, TokenAmount] {
  const [token0Deposited, token1Deposited] =
    !(totalBalance == null) &&
    !(userBalance == null) &&
    JSBI.greaterThanOrEqual(totalBalance.raw, userBalance.raw)
      ? [
          pair.getLiquidityValue(
            token0.address === pair.token0.address ? pair.token0 : pair.token1,
            totalBalance,
            userBalance,
            false
          ),
          pair.getLiquidityValue(
            token0.address === pair.token0.address ? pair.token1 : pair.token0,
            totalBalance,
            userBalance,
            false
          )
        ]
      : [undefined, undefined]

  if ((token0Deposited != null) || token1Deposited) undefined
  return [token0Deposited as TokenAmount, token1Deposited as TokenAmount]
}

export function convertSeconds (totalSeconds: number): string {
  // Handle negative input
  if (totalSeconds < 0) {
    throw new Error('Input must be a non-negative number')
  }

  // Calculate days
  const days = Math.floor(totalSeconds / (24 * 3600))
  totalSeconds %= (24 * 3600)

  // Calculate hours
  const hours = Math.floor(totalSeconds / 3600)
  totalSeconds %= 3600

  // Calculate minutes
  // const minutes = Math.floor(totalSeconds / 60)

  // // Remaining seconds
  // const seconds = totalSeconds % 60

  // Pad with zeros to ensure two-digit format
  const padZero = (num: number) => num.toString().padStart(2, '0')

  // Format the output
  return `Ends in ${days} Day and ${padZero(hours)} Hours `
}
